import { range, uniq, without } from 'lodash';
import { isTruthy } from '@ardoq/common-helpers';
import { useState } from 'react';
import {
  ExpandableRowData,
  ExpandableRowDataMeta,
  GenericRow,
  Row,
  RowData,
} from './types';

export const reduceWithColSpan = <TData extends RowData>(
  rowCells: Row<TData>
): Row<TData> => {
  const result: Row<TData> = [];
  let idx = 0;
  while (idx < rowCells.length) {
    const cell = rowCells[idx];
    result.push(cell);
    const span = cell.getColSpan
      ? cell.getColSpan(cell.value, cell.dataSourceRow)
      : 1;
    idx += Math.max(span, 1);
  }
  return result;
};

export const applyMeta = <TData extends RowData>(
  dataSource: (TData | undefined)[],
  idIndex = '_id',
  parent?: ExpandableRowData<TData>
): ExpandableRowData<TData>[] => {
  return dataSource.filter(isTruthy).map(rowData => {
    const id = idIndex
      ? rowData?.[idIndex as keyof TData]
      : dataSource.indexOf(rowData);
    const path = parent ? [parent.meta.path, id].join('.') : `${id}`;
    const level = parent ? parent.meta.level + 1 : 0;
    const meta: ExpandableRowDataMeta = {
      // @ts-expect-error todo
      ...(rowData.meta || {}),
      level,
      path,
    };
    const rowDataWithMeta = { ...rowData, meta };
    // @ts-expect-error TODO
    const children = rowData.children
      ? // @ts-expect-error TODO
        applyMeta(rowData.children, idIndex, rowDataWithMeta)
      : null;
    return { ...rowDataWithMeta, ...(children ? { children } : {}) };
  });
};

export const expandTree = <TData,>(
  dataSource: TData[] = [],
  expandedIds: string[] = [],
  expandedPartial: TData[] = [],
  idIndex?: string
) => {
  // Handle case if rowData.children is null
  return (dataSource ?? []).reduce((results, rowData) => {
    results.push(rowData);
    // @ts-expect-error TODO
    if (expandedIds.includes(rowData?.[idIndex || '_id'])) {
      // @ts-expect-error TODO
      expandTree(rowData.children, expandedIds, results, idIndex);
    }
    return results;
  }, expandedPartial);
};

export const collectExpandedIdsFromDataSource = (
  dataSource: GenericRow[],
  searchPhrase: string
) => {
  const ids: string[] = [];

  if (!searchPhrase) return ids;

  // it's faster to loop and push than create sub arrays and flatten
  const getRowIds = (row: GenericRow) => {
    if (row.children?.length && !row.meta?.dontExpandOnSearch) {
      if (row._id) ids.push(row._id);
      for (const child of row.children) getRowIds(child);
    }
  };

  for (const row of dataSource) getRowIds(row);

  return ids;
};

export const generateExpandFolder =
  (
    expandedFoldersIds: string[],
    setExpandedFoldersIds: (ids: string[]) => void
  ) =>
  (folderId: string, newOpenState?: boolean) => {
    if (expandedFoldersIds.includes(folderId) && newOpenState !== true) {
      setExpandedFoldersIds(without(expandedFoldersIds, folderId));
    } else {
      setExpandedFoldersIds([...expandedFoldersIds, folderId]);
    }
  };

export const useExpandFolder = (
  expandedFoldersIdsParam?: string[],
  setExpandedFoldersIdsParam?: (ids: string[]) => void
): [string[], (id: string) => void, (ids: string[]) => void] => {
  const expandedFoldersHook = useState<string[]>([]);
  const [expandedFoldersIds, setExpandedFoldersIds] =
    expandedFoldersIdsParam && setExpandedFoldersIdsParam
      ? [expandedFoldersIdsParam, setExpandedFoldersIdsParam]
      : expandedFoldersHook;

  const expandFolder = generateExpandFolder(
    expandedFoldersIds,
    setExpandedFoldersIds
  );

  return [expandedFoldersIds, expandFolder, setExpandedFoldersIds];
};

const getDistanceBetweenItems = (
  item1: string,
  item2: string,
  source: string[]
) => {
  if (item1 === item2) return 0;
  const item1Index = source.indexOf(item1);
  const item2Index = source.indexOf(item2);
  return Math.abs(item1Index - item2Index);
};

export const findClosest = (
  target: string,
  selectedSubset: string[],
  source: string[]
) => {
  const distances = selectedSubset.map(subsetItem =>
    getDistanceBetweenItems(target, subsetItem, source)
  );
  const bestDistance = Math.min(...distances);
  const indexOfBestDistance = distances.indexOf(bestDistance);
  return selectedSubset[indexOfBestDistance] || target;
};

export const findRangeSelectIds = (
  clickedId: string,
  selectedIds: string[],
  dataSourceIds: string[]
) => {
  const closestItem = findClosest(clickedId, selectedIds, dataSourceIds);
  const [from, to] = [
    dataSourceIds.indexOf(clickedId),
    dataSourceIds.indexOf(closestItem),
  ].sort((a, b) => a - b);
  const idsToSelect = range(from, to + 1).map(index => dataSourceIds[index]);
  return uniq([...selectedIds, ...idsToSelect]).filter(Boolean);
};
